/**
 * Quality tiers + live graphics settings (Graphics G4 + the Esc options menu).
 *
 * Five PRESETS (potato / low / med / high / ultra) each map to a `TierPreset`: a
 * `PostProcessingConfig` (AA + bloom/SSAO/DoF), a pixel-ratio cap, and the
 * LOD / shadow / cull / particle / fog / memory / streaming budgets the other
 * modules read via `activePreset()`.
 *
 * The Esc graphics menu lets the player pick a preset OR tweak an individual
 * feature. To support that, the ACTIVE settings are a live, mutable copy of the
 * chosen preset (`settings_`); a per-feature setter mutates it and flips the
 * state to "custom". `activePreset()` returns that live object, so every consumer
 * (PostModule / Lighting / Culling / Streaming / Weather / Atmosphere / lod) gets
 * the customised values the moment they re-read it (PostModule emits
 * `atlas:quality-changed` after any change, which the others listen to).
 *
 * Settings persist to localStorage and restore on boot; first run auto-detects a
 * starting preset from the device so a weak machine isn't dropped into Ultra.
 *
 * AA lives in the POST chain (FXAA/SMAA), MSAA OFF (`App.create antialias:false`) —
 * sidesteps the r182 "GTAO + WebGPU + MSAA → invalid WGSL" gotcha so SSAO can run.
 */
import type { PostProcessingConfig } from 'enginex/render/PostProcessingPipeline.js';

export type UpscaleMode = 'off' | 'ultra' | 'quality' | 'balanced' | 'performance';
export const UPSCALE_RATIO: Record<UpscaleMode, number> = { off: 1, ultra: 1.3, quality: 1.5, balanced: 1.7, performance: 2 };
export type Tier = 'minimal' | 'potato' | 'low' | 'med' | 'high' | 'ultra';

export interface TierPreset {
  post: PostProcessingConfig;
  pixelRatio: number;   // cap, clamped against devicePixelRatio at apply (the render-scale lever) — used when targetHeight is 0
  /**
   * The CANVAS height each tier targets, in pixels (2026-09-08, "potato and
   * minimal very low, ultra 4K"): the pixel ratio becomes targetHeight ÷ the
   * window's CSS height (clamped MIN..MAX_RENDER_SCALE), whatever the display —
   * ultra is a 3840×2160 canvas on a 1080p window (supersampled) and native on
   * a 4K one; minimal is 480p. 0 = native (the pixelRatio cap clamped to the
   * display). The FSR ratio then divides the SCENE below the canvas again.
   */
  targetHeight: number;
  /**
   * FSR 1 UPSCALING (perf frontier 2026-09-08, item 1): the scene pass renders
   * at 1/ratio of the drawing buffer and the engine's EASU + RCAS kernels
   * reconstruct the output size before any other pass. 'ultra' 1.3× /
   * 'quality' 1.5× / 'balanced' 1.7× / 'performance' 2×, 'off' = native.
   * Shading cost falls with the square of the ratio; the picture stays close
   * to native (the interim for temporal upscaling). Never applied in XR.
   */
  upscale: UpscaleMode;
  /** 'fsr' = spatial EASU/RCAS (default); 'taau' = the temporal resolve over the low-res pass (TRAA history + RCAS). */
  upscaleMode: 'fsr' | 'taau';
  /** Cloud raymarch resolution as a fraction of the drawing buffer (0.5 = a quarter of the rays); 1 = full. */
  cloudResolution: number;
  /**
   * Adaptive render scale: a frame-time governor moves the FSR ratio live
   * between the tier's own ratio and 2× when the machine cannot hold its
   * refresh rate, and probes back up when it can (lib/frame_governor.ts).
   */
  adaptive: boolean;
  particleCap: number;  // 0..1 scale on weather particle density (G4)
  fogScale: number;     // multiplier on atmosphere fog density (G4)
  cloudsEnabled: boolean; // volumetric clouds composite on/off (PostModule reads this)
  shadowsEnabled: boolean; // false = no CSM at all (potato) — the #2 GPU lever after render scale
  /**
   * Flat multiplier on sun + hemisphere-ambient intensity (LightingModule).
   * 1 everywhere except potato: with bloom, clouds, fog haze and surface detail
   * all stripped, the image loses the luminance those features carry and reads
   * uniformly dark — so potato compensates in the LIGHTS (free) instead of
   * re-enabling effects (which would defeat the tier). NOT a menu feature.
   */
  lightingBoost: number;
  /**
   * Multiplier on the settlement streaming radii (city 1000 / town 650 /
   * hamlet 300 m). Settlement content is INSTANCED and never per-object
   * culled, so `farCull` does not touch it — this is the only lever that
   * shrinks how much settlement draw-set is resident. Draw-ITEM count is the
   * render-thread bottleneck (three.js #30560), which pixel-side knobs
   * (render scale, shadows, post) cannot reduce.
   */
  settlementRadiusScale: number;
  /**
   * Always assemble settlements at the simplified LOD-1 variants, and never
   * swap back to LOD-0 up close. Kills BOTH costs of the two-level civ LOD:
   * the LOD-0 triangle load and the dispose→rebuild churn at the 380/460 m
   * hysteresis band (a whole-settlement teardown + 30-50 GLB reload — the
   * "sometimes lag while walking" hitch).
   */
  settlementForceLowLod: boolean;
  /**
   * Distant detail (2026-09-08, "show nothing past the drawing distance"):
   * true = the representations BEYOND the draw distance — settlement HLOD
   * prefabs + silhouette boxes, the terrain far tier and its far sea; false
   * = nothing past the draw distance (the fog closes there), and a
   * settlement builds only inside it. The in-range LOD ladder is untouched
   * either way — on a weak GPU it is what keeps a near city drawable.
   */
  distantDetail: boolean;
  /**
   * Admin-placement LOD (2026-09-08, the minimal tier): the cooked variant
   * every placed object renders at in play mode (0 = full … 3 = 5 %); props
   * fall back to their nearest lower level. Placements are ALSO distance-
   * culled by `farCull` (lib/placement_cull.ts).
   */
  placementLod: 0 | 1 | 2 | 3;
  /** Civ structures a settlement ASSEMBLES at: 1 (57 % of LOD0) or 2 (16 %); 2 also caps the in-range ladder at LOD2. */
  settlementBuildLod: 1 | 2;
  /** River ribbons: the engine water (the sea's material) or the cheap scrolled normal map. */
  riverWater: 'realistic' | 'simple';
  /** Terrain ground: the 37-sample climate/band stack, or ONE albedo sample tinted by the biome weights (applies at the next world entry). */
  groundQuality: 'full' | 'simple';
  /** Sky: the Hillaire atmosphere + IBL bake + night photo, or a flat sky the colour of the fog (applies at the next world entry). */
  skyQuality: 'full' | 'flat';
  /**
   * Colour LOOK (shader wave 2026-09-08): a procedural 3D LUT (lib/lut_looks)
   * handed to the engine's `lut3D` pass — 'none' removes the pass.
   */
  look: 'none' | 'cinematic' | 'warm' | 'cool' | 'noir' | 'cosmos' | 'vivid';
  /** Foliage translucency (leaves lit from behind) + hierarchical wind on the tree species (shader wave stage 2; applies as chunks stream). */
  foliageShading: boolean;
  /** Realistic fluid physics (2026-09-11): the camera-following interaction
   *  field (wakes, wading rings, splashes, rain on water), wind-driven sea
   *  state, swimming + wave-following rides. Off = today's water exactly. */
  fluidPhysics: boolean;
  /** Material effects on props/structures: snow accumulation by world-up + clearcoat on alloys, iridescence on bio-glass (applies to newly loaded assets). */
  materialFx: boolean;
  /** Interior mapping on window/glass materials (rooms behind the panes, lit by night) — shader wave stage 3; applies to newly loaded structures. */
  interiorMapping: boolean;
  /** VFX pack: soft particles (mist, tracers), dissolve on a creature's death, shield shell on a hull hit, heat haze behind a hovercraft, holograms over notice boards. */
  vfxShaders: boolean;
  /** Impact decals lit with baked crater normals (else unlit flat marks). */
  litDecals: boolean;
  /** Street lamps as spotlights projecting a fixture cookie (applies at the next world entry). */
  lampCookies: boolean;
  /** Persistent whitecap trails on the sea (a CPU foam grid the water samples; next world entry). */
  waterFoam: boolean;
  /** Projected caustics on the sea floor (an extra small render per frame; next world entry). */
  waterCaustics: boolean;
  /** Cloud shadows on the ground, foliage and structures from the cloud field's own weather map (shader wave stage 4). */
  cloudShadows: boolean;
  /** Stochastic (Deliot–Heitz) sampling of the ground albedos — no visible tile grid (applies at the next world entry). */
  groundAntiTiling: boolean;
  /** Raindrop ripple rings on up-facing ground while it rains (applies at the next world entry). */
  rainRipples: boolean;
  /** Aurora curtains on some Commons nights. */
  aurora: boolean;
  /** Raymarched nebula volumes at the space nebula sites (next sector entry). */
  nebulae: boolean;
  /** O'Neil atmosphere shells on the planets seen from orbit (next sector entry). */
  planetAtmosphere: boolean;
  /** Light shafts around the camera while submerged (next world entry). */
  underwaterShafts: boolean;
  /** Dual-quaternion skinning on the near versona actors (new actors). */
  dqSkinning: boolean;
  // ── Shader additions wave (2026-09-15) ──
  /** Toksvig normal-map roughness filtering — specular sparkle at the source (materials built after the flag). */
  specularAA: boolean;
  /** Footprint-filtered procedural detail: no shimmer at distance, unresolved bumps become roughness (next parse). */
  proceduralFiltering: boolean;
  /** The shadowed light VOLUME around the player — sun shafts, lamp cones (live). */
  volumetricLights: boolean;
  /** Six-way lit smoke on impacts and blown dust (new emitters). */
  litSmoke: boolean;
  /** LTC rect area lights on lit windows at night (next world entry — the light-set identity law). */
  areaLights: boolean;
  /** Real transmission on prop / decor glass and canopies (next parse). */
  glassTransmission: boolean;
  /** Brushed-metal anisotropy on metallic props and weapons (next parse). */
  hardSurfaceAnisotropy: boolean;
  /** Light-return lobe on signs and markers (next parse). */
  retroreflection: boolean;
  // ── Render wave 1 (2026-09-17) ──
  /** Blend the three nearest impostor frames instead of snapping to one (far trees; next bake). */
  impostorBlend: boolean;
  /** A top-down roof map around the player: rain and snow stop under cover and the ground under it stays dry (live). */
  rainShelter: boolean;
  /** Exposure metering: the log average, or the percentile-trimmed histogram. */
  autoExposureMetering: 'average' | 'histogram';
  meshSmoothing: boolean; // loader-time creased normals on structure GLBs (one-time cost, ~0 runtime)
  surfaceDetail: boolean; // TSL procedural detail-normal + roughness breakup (per-fragment ALU)
  /**
   * 0..1 scale on chunk-foliage scatter density. Foliage is the TRIANGLE WHALE
   * of the whole frame — the per-tier census showed one shared-instancer group
   * of ~4,270 trees carrying 64.5M of coast-surf's 64.7M triangles at EVERY
   * tier (≈15k tri per tree, no LOD, per-chunk culling traded away in the
   * instancer consolidation). This is the one knob that shrinks it. Consumed
   * live by EnvironmentModule.scatterChunkFoliage; a tier switch re-scatters.
   */
  foliageDensityScale: number;
  // ── G5: scale the rest of the pipeline (LOD / shadows / culling / texture / memory / streaming) ──
  lodDistanceScale: number; // G2 lib/lod buildLOD distance multiplier (lower = swap to low-LOD sooner)
  shadowMapSize: number;    // G3 CSM per-cascade shadow map size
  shadowCascades: number;   // G3 CSM cascade count
  shadowMaxFar: number;     // G3 CSM far distance
  farCull: number;          // G2 RenderCulling distance cutoff (m) — the max object draw distance
  /** Commons terrain streaming window RADIUS in 256 m chunks (draw distance for the ground). */
  chunkRadius: number;
  /** Foliage scatters only within this Chebyshev ring — kept small so extending
   *  chunkRadius grows the terrain footprint WITHOUT multiplying the tree whale. */
  foliageRing: number;
  maxTextureRes: number;    // ENFORCED: AssetModule caps compressed mip chains to this at parse (texture_budget.ts)
  /**
   * Settlement study R2: the LOD the NEAR foliage ring and settlement props
   * load. 1 = the cooked LOD1 variant (half the triangles, 512² textures —
   * a shrub is 0.8 MB instead of 10 MB); 0 = the canonical LOD0. The far
   * foliage ring is always LOD1. High/ultra keep LOD0 up close.
   */
  /**
   * Cap on the near-skinned versona ACTORS (`lib/versona_rig.ts`): wave-2
   * bodies are ~290k-triangle SkinnedMeshes, so 24 of them is a desktop-GPU
   * budget; lower tiers draw fewer as actors and the rest as instanced twins.
   */
  nearActors: number;
  /**
   * LOD the near foliage ring loads: 0 canonical, 1 half, 2 fifteen percent.
   * Foliage is the frame's triangle whale (a census found 78 % of a live
   * frame's triangles in 151 foliage instances), so the cheap tiers take 2.
   */
  nearFoliageLod: 0 | 1 | 2;
  /** Props and decor inside settlements: 1 = LOD1 variant, 0 = canonical. */
  /** Prop/decor LOD in settlements: 0 full, 1 = 50 %, 2 = 15 % of LOD0 (real since the 2026-09-18 hinterland re-cook). */
  propLod: 0 | 1 | 2;
  /** The far versona body (instanced twin): 0 = the full 290k mesh ("we want 300k vertices" — high/ultra),
   *  1 = the cooker's LOD1 (~145k), 2 = LOD2 (~45k, seam-respecting, compare-verified). A software or
   *  weak GPU cannot parse and draw eighteen 290k bodies; the near actors are unaffected. */
  farBodyLod: 0 | 1 | 2;
  drawCallBudget: number;   // G2 draw-call ceiling (HUD warn threshold)
  memoryBudgetMb: number;   // G5 VRAM budget (LRU evicts above it)
  prefetchRegions: number;  // G5 regions to prefetch ahead along the movement vector
  /**
   * Hillaire aerial-perspective composite (distance haze on geometry). The
   * engine bakes the froxel volume only when the sky module was CREATED with
   * it on — this flag gates that at boot AND the PostModule composite pass,
   * so a runtime change takes effect after reload (the menu says so).
   */
  aerialEnabled: boolean;
  /** Auto-exposure eye adaptation (engine AutoExposureSystem). Opt-in. */
  autoExposure: boolean;
  /**
   * Second LOW scattered cloud layer (~800-1500 m). The high deck sits at
   * 3800-9600 m and barely parallaxes at walk speed; the low puffs swing ~4°
   * per 50 m walked, making the sky's world-anchoring legible. Costs a
   * second (cheap-preset) raymarch, so med keeps it off.
   */
  lowClouds: boolean;
  /**
   * Cloud camera-translation response (engine parallaxScale, live uniform).
   * 0 = frozen to the camera (HDRP "Rendering Space: Camera"); 1 = full world
   * parallax — clouds are STATIONARY in the world (the 2026-08-19 shipped
   * default, per user directive: the 0.15 "travel evolution" compromise was
   * perceptually indistinguishable from camera-glued at walking scale and read
   * as "the clouds follow me"). Walk-speed parallax is physically small on the
   * 3.8 km deck (moon-follows-the-car); the low puffs (~4° per 50 m) make the
   * anchoring legible. Taste knob, same on every tier.
   */
  cloudParallax: number;
  /** AA quality 2|4|8|16|32 — iterated edge-resolve passes (engine AA section). */
  aaQuality: number;
  /** Auto-exposure adaptation strength 0..1 (1 = full eye adaptation). */
  autoExposureStrength: number;
  // ── Graphics-menu completion wave (all optional — preset defaults apply) ──
  /** Texture anisotropy 1|2|4|8|16 (AssetModule applies at parse; a quality
   *  change re-streams via the StreamingModule evict). Default 4. */
  anisotropy?: number;
  /** Renderer tone-mapping exposure (LightingModule applies live). Default 0.85. */
  tonemapExposure?: number;
  /** Renderer tone-mapping operator. Default 'aces'. */
  tonemapOperator?: 'aces' | 'agx' | 'neutral' | 'reinhard';
  /** Volumetric-cloud raymarch quality override ('auto' = the tier mapping). */
  cloudMarchQuality?: 'auto' | 'low' | 'medium' | 'high';
  /** Planar water reflections (heavy — AtlasWaterModule rebuilds on change). */
  waterReflections?: boolean;
  /** Colourblind daltonization (engine ColorblindFilterNode, display-referred). */
  colorblindMode?: 'off' | 'protanopia' | 'deuteranopia' | 'tritanopia';
  colorblindStrength?: number;
  /** Camera vertical FOV in degrees; 0 = leave the engine default. */
  cameraFov?: number;
  /**
   * Tree/foliage WIND, on its own switch (2026-09-14). Unset = follow
   * `foliageShading`, which is how every stock preset behaves; setting it
   * lets a custom profile keep the cheap tiers and still have the trees move
   * (or run the leaf lighting with the trees still). Read it through
   * `foliageWindOn()`, never directly.
   */
  foliageWind?: boolean;
  /** Adaptive auto-tune: walk the tier ladder to hold ~50+ fps. */
  autoTune?: boolean;
}

export const TIERS: Record<Tier, TierPreset> = {
  // Potato: every knob floored so a ~3.2 GHz quad-core with a mid-range GPU runs
  // lag-free. Levers ranked by measured impact: render scale (pixelRatio 0.5 =
  // quarter the fragments), shadows fully OFF (no CSM passes at all), no bloom/
  // AO/DoF (the engine pipeline still applies its default FXAA, which smooths the
  // 0.5-scale upsample), shortest draw distance the slider allows, no clouds/
  // particles/surface detail, earliest LOD swap, halved texture + memory budgets.
  // lightingBoost 1.35 compensates the luminance bloom/clouds/haze carry at
  // higher tiers — without it the tier reads uniformly dark.
  // MINIMAL — below potato (2026-09-08, "implement minimal graphics mode …
  // everything on lowest settings"): every lever at its floor, the void past
  // 500 m, placements at 5 % detail and culled by distance, settlements
  // assembled at LOD2, no skinned actors, the cheap river, a one-sample
  // ground and a flat sky. The in-range LOD ladder still runs (capped at 2).
  minimal: {
    post: { fxaa: { enabled: false } },
    targetHeight: 480, pixelRatio: 0.5, upscale: 'performance', upscaleMode: 'fsr', cloudResolution: 0.25, adaptive: true, particleCap: 0, fogScale: 0.7, cloudsEnabled: false,
    shadowsEnabled: false, lightingBoost: 1.35,
    settlementRadiusScale: 0.5, settlementForceLowLod: true, distantDetail: false, placementLod: 3, settlementBuildLod: 2, riverWater: 'simple', groundQuality: 'simple', skyQuality: 'flat', look: 'none', foliageShading: false, fluidPhysics: false, materialFx: false, interiorMapping: false, vfxShaders: false, litDecals: false, lampCookies: false, waterFoam: false, waterCaustics: false, cloudShadows: false, groundAntiTiling: false, rainRipples: false, aurora: false, nebulae: false, planetAtmosphere: false, underwaterShafts: false, dqSkinning: false, specularAA: false, proceduralFiltering: false, volumetricLights: false, litSmoke: false, areaLights: false, glassTransmission: false, hardSurfaceAnisotropy: false, retroreflection: false, impostorBlend: false, rainShelter: false, autoExposureMetering: 'average',
    meshSmoothing: false, surfaceDetail: false, foliageDensityScale: 0,
    lodDistanceScale: 0.5, shadowMapSize: 256, shadowCascades: 1, shadowMaxFar: 40,
    farCull: 500, chunkRadius: 2, foliageRing: 1, maxTextureRes: 512, nearFoliageLod: 2, propLod: 2, farBodyLod: 2, nearActors: 0, drawCallBudget: 1500, memoryBudgetMb: 96, prefetchRegions: 0,
    aerialEnabled: false, autoExposure: false, lowClouds: false, cloudParallax: 1.0, aaQuality: 2, autoExposureStrength: 1, anisotropy: 1,
  },
  potato: {
    post: {},
    targetHeight: 720, pixelRatio: 1, upscale: 'balanced', upscaleMode: 'fsr', cloudResolution: 0.25, adaptive: true, particleCap: 0, fogScale: 0.7, cloudsEnabled: false,
    shadowsEnabled: false, lightingBoost: 1.35,
    settlementRadiusScale: 0.6, settlementForceLowLod: true, distantDetail: true, placementLod: 2, settlementBuildLod: 1, riverWater: 'realistic', groundQuality: 'full', skyQuality: 'full', look: 'none', foliageShading: false, fluidPhysics: false, materialFx: false, interiorMapping: false, vfxShaders: false, litDecals: false, lampCookies: false, waterFoam: false, waterCaustics: false, cloudShadows: false, groundAntiTiling: false, rainRipples: false, aurora: false, nebulae: false, planetAtmosphere: false, underwaterShafts: false, dqSkinning: false, specularAA: false, proceduralFiltering: false, volumetricLights: false, litSmoke: false, areaLights: false, glassTransmission: false, hardSurfaceAnisotropy: false, retroreflection: false, impostorBlend: false, rainShelter: false, autoExposureMetering: 'average',
    meshSmoothing: false, surfaceDetail: false, foliageDensityScale: 0.35,
    lodDistanceScale: 1.0, shadowMapSize: 512, shadowCascades: 1, shadowMaxFar: 80,
    farCull: 500, chunkRadius: 3, foliageRing: 1, maxTextureRes: 512, nearFoliageLod: 2, propLod: 2, farBodyLod: 2, nearActors: 4, drawCallBudget: 2500, memoryBudgetMb: 128, prefetchRegions: 0,
    aerialEnabled: false, autoExposure: false, lowClouds: false, cloudParallax: 1.0, aaQuality: 2, autoExposureStrength: 1, anisotropy: 2,
  },
  low: {
    post: { bloom: { mode: 'kawase', threshold: 1.1, strength: 0.35, radius: 1 }, fxaa: { enabled: true } },
    targetHeight: 1080, pixelRatio: 1, upscale: 'quality', upscaleMode: 'fsr', cloudResolution: 0.25, adaptive: true, particleCap: 0.25, fogScale: 0.85, cloudsEnabled: true,
    shadowsEnabled: true, lightingBoost: 1,
    // ForceLowLod on: the low tier caps textures at 1024 anyway, so the 22 MB
    // LOD0 civ GLBs (16× 2K KTX2 inside) paid full download+decode for pixels
    // the tier then threw away — the 1.9 MB LOD1 variants ARE the 1024-class
    // set (11× less transfer; the Quest browser auto-detects this tier).
    settlementRadiusScale: 0.75, settlementForceLowLod: true, distantDetail: true, placementLod: 2, settlementBuildLod: 1, riverWater: 'realistic', groundQuality: 'full', skyQuality: 'full', look: 'none', foliageShading: true, fluidPhysics: true, materialFx: false, interiorMapping: false, vfxShaders: true, litDecals: false, lampCookies: false, waterFoam: false, waterCaustics: false, cloudShadows: true, groundAntiTiling: false, rainRipples: true, aurora: false, nebulae: false, planetAtmosphere: false, underwaterShafts: false, dqSkinning: false, specularAA: true, proceduralFiltering: true, volumetricLights: false, litSmoke: false, areaLights: false, glassTransmission: false, hardSurfaceAnisotropy: false, retroreflection: false, impostorBlend: false, rainShelter: true, autoExposureMetering: 'average',
    meshSmoothing: false, surfaceDetail: false, foliageDensityScale: 0.6,
    lodDistanceScale: 1.6, shadowMapSize: 1024, shadowCascades: 2, shadowMaxFar: 150,
    farCull: 800, chunkRadius: 3, foliageRing: 2, maxTextureRes: 1024, nearFoliageLod: 2, propLod: 1, farBodyLod: 1, nearActors: 8, drawCallBudget: 5000, memoryBudgetMb: 256, prefetchRegions: 1,
    aerialEnabled: false, autoExposure: false, lowClouds: false, cloudParallax: 1.0, aaQuality: 2, autoExposureStrength: 1, anisotropy: 4, cloudMarchQuality: 'low',
  },
  med: {
    post: {
      smaa: { enabled: true, quality: 2 }, // quality = this tier's aaQuality: the PRESET must carry it or the pipeline never sees it (2026-09-15)
      bloom: { threshold: 1.1, strength: 0.4, radius: 0.5 }, // threshold 1.1: bloom only true emitters (sun/stars/lava), not the bright sky dome
      // Ladder (2026-09-09): half-res GTAO at the XeGTAO low preset (8 spp) and the analytic height fog — cheap at 1440p.
      ao: { intensity: 0.9, radius: 0.45, samples: 8, resolutionScale: 0.5 },
      volumetricFog: { density: 0.010, heightFalloff: 0.05, sunPower: 6 },
    },
    targetHeight: 1440, pixelRatio: 1.5, upscale: 'quality', upscaleMode: 'fsr', cloudResolution: 0.5, adaptive: true, particleCap: 0.6, fogScale: 1.0, cloudsEnabled: true,
    shadowsEnabled: true, lightingBoost: 1,
    settlementRadiusScale: 1.25, settlementForceLowLod: false, distantDetail: true, placementLod: 1, settlementBuildLod: 1, riverWater: 'realistic', groundQuality: 'full', skyQuality: 'full', look: 'none', foliageShading: true, fluidPhysics: true, materialFx: true, interiorMapping: true, vfxShaders: true, litDecals: true, lampCookies: false, waterFoam: false, waterCaustics: false, cloudShadows: true, groundAntiTiling: true, rainRipples: true, aurora: false, nebulae: false, planetAtmosphere: true, underwaterShafts: true, dqSkinning: false, specularAA: true, proceduralFiltering: true, volumetricLights: false, litSmoke: true, areaLights: false, glassTransmission: false, hardSurfaceAnisotropy: true, retroreflection: true, impostorBlend: true, rainShelter: true, autoExposureMetering: 'histogram',
    meshSmoothing: true, surfaceDetail: true, foliageDensityScale: 0.85,
    lodDistanceScale: 2.2, shadowMapSize: 2048, shadowCascades: 3, shadowMaxFar: 200,
    farCull: 1300, chunkRadius: 5, foliageRing: 2, maxTextureRes: 2048, nearFoliageLod: 1, propLod: 1, farBodyLod: 1, nearActors: 12, drawCallBudget: 10000, memoryBudgetMb: 512, prefetchRegions: 1,
    aerialEnabled: true, autoExposure: false, lowClouds: false, cloudParallax: 1.0, aaQuality: 2, autoExposureStrength: 1, anisotropy: 8,
  },
  high: {
    post: {
      smaa: { enabled: true, quality: 4 },
      bloom: { threshold: 1.1, strength: 0.5, radius: 0.6 },
      ao: { intensity: 1.0, radius: 0.5, samples: 16, resolutionScale: 0.5 }, // half-res GTAO (perf frontier item 10)
      // `focalLength` is the FOCUS RANGE in METRES, not a lens length: three
      // computes CoC = smoothstep(0, focalLength, |viewZ| − focusDistance), so
      // 0.05 meant "completely out of focus 5 cm off the 18 m plane" — i.e.
      // the whole world blurred on the DEFAULT tier (2026-09-14). 55 m keeps
      // the near foreground and the far distance soft while gameplay range
      // stays sharp.
      dof: { focusDistance: 18, focalLength: 55, bokehScale: 1.2 },
      // Shader wave 2026-09-08: the engine passes that were built and menu-exposed but off in every preset.
      volumetricFog: { density: 0.012, heightFalloff: 0.05, sunPower: 8 }, // analytic height fog + sun in-scatter
      contactShadows: { enabled: true },
      lensFlares: { enabled: true },
    },
    targetHeight: 1800, pixelRatio: 2, upscale: 'quality', upscaleMode: 'fsr', cloudResolution: 0.5, adaptive: true, particleCap: 1.0, fogScale: 0.6, cloudsEnabled: true,
    shadowsEnabled: true, lightingBoost: 1,
    settlementRadiusScale: 1.7, settlementForceLowLod: false, distantDetail: true, placementLod: 0, settlementBuildLod: 1, riverWater: 'realistic', groundQuality: 'full', skyQuality: 'full', look: 'none', foliageShading: true, fluidPhysics: true, materialFx: true, interiorMapping: true, vfxShaders: true, litDecals: true, lampCookies: true, waterFoam: true, waterCaustics: false, cloudShadows: true, groundAntiTiling: true, rainRipples: true, aurora: true, nebulae: true, planetAtmosphere: true, underwaterShafts: true, dqSkinning: true, specularAA: true, proceduralFiltering: true, volumetricLights: true, litSmoke: true, areaLights: true, glassTransmission: true, hardSurfaceAnisotropy: true, retroreflection: true, impostorBlend: true, rainShelter: true, autoExposureMetering: 'histogram',
    meshSmoothing: true, surfaceDetail: true, foliageDensityScale: 1.0,
    lodDistanceScale: 3.0, shadowMapSize: 4096, shadowCascades: 4, shadowMaxFar: 300,
    farCull: 1800, chunkRadius: 7, foliageRing: 3, maxTextureRes: 2048, nearFoliageLod: 0, propLod: 0, farBodyLod: 0, nearActors: 18, drawCallBudget: 20000, memoryBudgetMb: 1024, prefetchRegions: 2,
    aerialEnabled: true, autoExposure: false, lowClouds: true, cloudParallax: 1.0, aaQuality: 4, autoExposureStrength: 1, anisotropy: 16,
  },
  ultra: {
    post: {
      smaa: { enabled: true, quality: 4 },
      bloom: { threshold: 1.1, strength: 0.55, radius: 0.65 },
      ao: { intensity: 1.1, radius: 0.6, samples: 24, resolutionScale: 0.5 },
      dof: { focusDistance: 18, focalLength: 45, bokehScale: 1.4 }, // focus RANGE in metres — see the high tier
      // Shader wave 2026-09-08: everything the engine has, on at ultra.
      volumetricFog: { density: 0.012, heightFalloff: 0.05, sunPower: 8 },
      contactShadows: { enabled: true },
      lensFlares: { enabled: true },
      ssr: { intensity: 1.0, maxDistance: 40, thickness: 0.35, resolutionScale: 0.5 }, // 1.0 (was 0.6): the composite REPLACES the IBL specular on a hit (render wave 1), the damping compensated for a double count
      godRays: { enabled: true, numSamples: 48, decay: 0.965, exposure: 0.22, density: 1.0, weight: 0.01 },
      // `temporal: false` (2026-09-14). three's SSGINode uses temporal filtering
      // to JITTER its slice direction and spatial offset per frame (a 6-frame
      // rotation, a 4-frame offset) on the assumption that TRAA resolves the
      // result. This tier antialiases with SMAA and allocates no velocity
      // target, so nothing ever averaged those frames — the jitter just
      // shimmered. Pinned sampling is stable instead. Flip back to true only
      // together with TRAA (which additionally needs real per-instance motion
      // vectors — the crowd instancer currently pins them to zero).
      // NB the comment's "half-res" was never true either: SSGINode re-sizes
      // its target to the full drawing buffer in updateBefore and exposes no
      // resolution scale.
      ssgi: { enabled: true, sliceCount: 2, stepCount: 8, temporal: false, intensity: 1, aoStrength: 0.5 },
    },
    targetHeight: 2160, pixelRatio: 2, upscale: 'ultra', upscaleMode: 'fsr', cloudResolution: 0.75, adaptive: true, particleCap: 1.0, fogScale: 0.45, cloudsEnabled: true,
    shadowsEnabled: true, lightingBoost: 1,
    settlementRadiusScale: 2.5, settlementForceLowLod: false, distantDetail: true, placementLod: 0, settlementBuildLod: 1, riverWater: 'realistic', groundQuality: 'full', skyQuality: 'full', look: 'none', foliageShading: true, fluidPhysics: true, materialFx: true, interiorMapping: true, vfxShaders: true, litDecals: true, lampCookies: true, waterFoam: true, waterCaustics: true, cloudShadows: true, groundAntiTiling: true, rainRipples: true, aurora: true, nebulae: true, planetAtmosphere: true, underwaterShafts: true, dqSkinning: true, specularAA: true, proceduralFiltering: true, volumetricLights: true, litSmoke: true, areaLights: true, glassTransmission: true, hardSurfaceAnisotropy: true, retroreflection: true, impostorBlend: true, rainShelter: true, autoExposureMetering: 'histogram',
    meshSmoothing: true, surfaceDetail: true, foliageDensityScale: 1.15,
    lodDistanceScale: 4.0, shadowMapSize: 4096, shadowCascades: 4, shadowMaxFar: 400,
    // DRAW DISTANCE HALVED (2026-09-17, "full draw distance is too far"): ultra 20 → 10 chunks (2.56 km),
    // and every tier below scaled with it (high 12 → 7, med 6 → 5, low 4 → 3; potato/minimal at the 500 m floor);
    // farCull follows the window, and settlementRadiusScale keeps a city's build band INSIDE the window.
    // The Draw-distance slider still reaches 5 km (setDrawDistance).
    farCull: 2560, chunkRadius: 10, foliageRing: 3, maxTextureRes: 4096, nearFoliageLod: 0, propLod: 0, farBodyLod: 0, nearActors: 24, drawCallBudget: 40000, memoryBudgetMb: 2048, prefetchRegions: 3,
    aerialEnabled: true, autoExposure: false, lowClouds: true, cloudParallax: 1.0, aaQuality: 4, autoExposureStrength: 1, anisotropy: 16, waterReflections: true, cloudMarchQuality: 'high',
  },
};

export const TIER_ORDER: Tier[] = ['minimal', 'potato', 'low', 'med', 'high', 'ultra'];
export const TIER_LABEL: Record<Tier, string> = { minimal: 'Minimal', potato: 'Potato', low: 'Low', med: 'Medium', high: 'High', ultra: 'Ultra' };
export const DEFAULT_TIER: Tier = 'high';
export function nextTier(t: Tier): Tier { const i = TIER_ORDER.indexOf(t); return TIER_ORDER[(i + 1) % TIER_ORDER.length]; }

/** Deep-enough clone so per-feature mutations never write back into the shared TIERS preset. */
function clonePreset(p: TierPreset): TierPreset {
  return { ...p, post: { ...p.post } };
}

// ── Live active settings ──────────────────────────────────────────────────────
// `settings_` is the active TierPreset — a copy of the chosen preset, mutated in
// place by the per-feature setters. `custom_` flips true once any feature differs
// from a clean preset. `baseTier_` is the preset the customisation started from.
let baseTier_: Tier = DEFAULT_TIER;
let custom_ = false;
let settings_: TierPreset = clonePreset(TIERS[DEFAULT_TIER]);

/** The active (possibly customised) settings — every graphics consumer reads this. */
export function activePreset(): TierPreset { return settings_; }
/** The base preset the active settings derive from. */
export function activeTier(): Tier { return baseTier_; }
/** True when an individual feature was tweaked away from a clean preset. */
export function isCustom(): boolean { return custom_; }

/** Apply a clean preset (clears any per-feature customisation). */
export function setActiveTier(t: Tier): void {
  // Session-sticky preferences survive a preset switch (presets don't carry
  // them): auto-tune, colourblind correction, FOV, tone-map taste.
  const keep = {
    autoTune: settings_?.autoTune, colorblindMode: settings_?.colorblindMode,
    colorblindStrength: settings_?.colorblindStrength, cameraFov: settings_?.cameraFov,
    tonemapOperator: settings_?.tonemapOperator, tonemapExposure: settings_?.tonemapExposure,
  };
  baseTier_ = t;
  custom_ = false;
  settings_ = clonePreset(TIERS[t]);
  for (const [k, v] of Object.entries(keep)) {
    if (v !== undefined) (settings_ as unknown as Record<string, unknown>)[k] = v;
  }
  saveSettings();
}

// ── The graphics DRAFT — what the Esc menu's "Apply changes" button applies (2026-09-20) ──────────────
// WHY: every graphics change is a post-pipeline REBUILD (TSL is compiled per configuration) plus an
// `atlas:quality-changed` (warm assets evicted, the sky LUTs re-baked) — and ~35 flag rows never announced
// themselves at all, so a toggle "did nothing" until some other row happened to rebuild. The menu now edits a
// DRAFT and one button applies it: one rebuild for any number of changes, and nothing half-applied.
//
// HOW: `settings_` stays THE LIVE OBJECT for the 47 modules that read it. The draft is swapped IN only for the
// duration of a synchronous callback (`inDraft`): the menu's render and its setter calls run inside it, so every
// existing setter, every `current*()` read and `setActiveTier` work on the draft UNCHANGED — a staged preset even
// shows its own values in the rows. Nothing else can run during the swap (one thread, no awaits), and
// `saveSettings` stands down inside it, so an un-applied draft is never persisted or seen by the game.
interface DraftState { baseTier: Tier; custom: boolean; settings: TierPreset }
let draft_: DraftState | null = null;
let draftBase_: DraftState | null = null; // the live state the draft was cut from — what "changed" is measured against
let inDraft_ = false;
let liveStamp_ = 0;        // bumped by every LIVE mutation (auto-tune, a probe lever, the tours)
let draftStamp_ = -1;

const snapshotLive = (): DraftState => ({ baseTier: baseTier_, custom: custom_, settings: clonePreset(settings_) });
const same = (a: unknown, b: unknown): boolean => a === b || JSON.stringify(a) === JSON.stringify(b);

/** The keys a draft changed against a base: top-level settings, `post.<pass>`, and `preset` for a tier switch. */
function diffKeys(d: DraftState, base: DraftState): string[] {
  const out: string[] = [];
  if (d.baseTier !== base.baseTier || (base.custom && !d.custom)) out.push('preset');
  const a = d.settings as unknown as Record<string, unknown>, b = base.settings as unknown as Record<string, unknown>;
  for (const k of new Set([...Object.keys(a), ...Object.keys(b)])) {
    if (k === 'post') continue;
    if (!same(a[k], b[k])) out.push(k);
  }
  const pa = d.settings.post as unknown as Record<string, unknown>, pb = base.settings.post as unknown as Record<string, unknown>;
  for (const k of new Set([...Object.keys(pa), ...Object.keys(pb)])) if (!same(pa[k], pb[k])) out.push(`post.${k}`);
  return out;
}

/** Live settings moved under an open draft (auto-tune stepped the tier, a lever fired): keep what the PLAYER changed, take the rest from live. */
function rebaseDraft(): void {
  if (!draft_ || !draftBase_ || draftStamp_ === liveStamp_) return;
  const changed = diffKeys(draft_, draftBase_);
  const next = snapshotLive();
  if (changed.includes('preset')) { next.baseTier = draft_.baseTier; next.custom = draft_.custom; next.settings = clonePreset(draft_.settings); }
  else {
    const to = next.settings as unknown as Record<string, unknown>, from = draft_.settings as unknown as Record<string, unknown>;
    for (const k of changed) {
      if (k.startsWith('post.')) { const pk = k.slice(5); const v = (from.post as Record<string, unknown>)[pk]; if (v === undefined) delete (to.post as Record<string, unknown>)[pk]; else (to.post as Record<string, unknown>)[pk] = v; }
      else to[k] = from[k];
    }
    if (changed.length) next.custom = true;
  }
  draftBase_ = snapshotLive(); draft_ = next; draftStamp_ = liveStamp_;
}

/** Run `fn` with the DRAFT as the active settings (setters and reads alike). Opens a draft on first use. */
export function inDraft<T>(fn: () => T): T {
  if (inDraft_) return fn();
  if (!draft_) { draft_ = snapshotLive(); draftBase_ = snapshotLive(); draftStamp_ = liveStamp_; }
  rebaseDraft();
  const live: DraftState = { baseTier: baseTier_, custom: custom_, settings: settings_ };
  baseTier_ = draft_.baseTier; custom_ = draft_.custom; settings_ = draft_.settings; inDraft_ = true; liveHold_ = live;
  try { return fn(); }
  finally {
    draft_ = { baseTier: baseTier_, custom: custom_, settings: settings_ }; // setActiveTier REPLACES the object
    baseTier_ = live.baseTier; custom_ = live.custom; settings_ = live.settings; inDraft_ = false; liveHold_ = null;
  }
}
/** Inside `inDraft`, read something off the LIVE settings (a row's "was …"). Outside it, just runs `fn`. */
export function inLive<T>(fn: () => T): T {
  if (!inDraft_ || !draft_) return fn();
  const d: DraftState = { baseTier: baseTier_, custom: custom_, settings: settings_ };
  const live = liveHold_!;
  baseTier_ = live.baseTier; custom_ = live.custom; settings_ = live.settings;
  try { return fn(); } finally { baseTier_ = d.baseTier; custom_ = d.custom; settings_ = d.settings; }
}
let liveHold_: DraftState | null = null;
/** What the draft would change, as setting keys ('shadowMapSize', 'post.bloom', 'preset'). Empty = nothing to apply. */
export function draftChanges(): string[] { rebaseDraft(); return draft_ && draftBase_ ? diffKeys(draft_, draftBase_) : []; }
/** The draft becomes the live settings and is persisted. The caller rebuilds the pipeline (PostModule.applySettings). */
export function commitDraft(): boolean {
  const changed = draftChanges();
  if (!draft_ || changed.length === 0) { draft_ = null; draftBase_ = null; return false; }
  baseTier_ = draft_.baseTier; custom_ = draft_.custom; settings_ = draft_.settings;
  draft_ = null; draftBase_ = null; liveStamp_++;
  saveSettings();
  return true;
}
/** Throw the draft away: the menu shows the live settings again. */
export function discardDraft(): void { draft_ = null; draftBase_ = null; }

// ── Per-feature setters (Esc menu "Custom") ───────────────────────────────────
// Each mutates the live settings + flips `custom_` + persists. PostModule.applySettings()
// rebuilds the pipeline + re-emits atlas:quality-changed after any of these.

export type AAMode = 'off' | 'fxaa' | 'smaa' | 'traa';
export type ShadowLevel = 'off' | 'low' | 'med' | 'high' | 'ultra';

function markCustom(): void { custom_ = true; saveSettings(); }

/** Render-scale (the pixel-ratio cap) — the single biggest perf lever. 0.5..2.0. */
/**
 * THE render-scale rule (2026-09-08, "fix the render scale cap so the canvas
 * actually shrinks on potato"): the tier's cap clamped to the display's device
 * pixel ratio — ultra alone may exceed it (true supersampling). ONE function so
 * the boot config (App.create), PostModule's apply and every re-apply agree.
 * Measured before: the cap landed only inside the first pipeline build, AFTER
 * the pipeline had measured the uncapped buffer — the menu and the world
 * streaming behind it rendered at the full display ratio.
 */
export function effectivePixelRatio(
  dpr: number = (typeof window !== 'undefined' ? window.devicePixelRatio : 1) || 1,
  cssHeight: number = (typeof window !== 'undefined' ? window.innerHeight : 1080) || 1080,
): number {
  // Resolution TARGET mode (the tiers' default): the canvas height the tier
  // wants, whatever the display — "potato and minimal very low, ultra 4K".
  const target = settings_.targetHeight ?? 0;
  if (target > 0) return clampNum(target / Math.max(1, cssHeight), MIN_RENDER_SCALE, MAX_RENDER_SCALE);
  // Ratio mode (the Render-scale slider): the cap clamped to the display, ultra alone above it.
  const cap = settings_.pixelRatio;
  return activeTier() === 'ultra' ? cap : Math.min(dpr, cap);
}
/** The canvas height effectivePixelRatio yields for a window (probes/menus). */
export function effectiveCanvasHeight(cssHeight: number, dpr = 1): number { return Math.floor(cssHeight * effectivePixelRatio(dpr, cssHeight)); }
export const MIN_RENDER_SCALE = 0.2; // minimal at 480p on a 4K CSS window needs 0.22
export const MAX_RENDER_SCALE = 3; // ultra's 4K on a 720-px-high window
export const RESOLUTION_TARGETS: Array<{ height: number; label: string }> = [
  { height: 480, label: '480p' }, { height: 720, label: '720p' }, { height: 1080, label: '1080p' }, { height: 1440, label: '1440p' }, { height: 1800, label: '1800p' }, { height: 2160, label: '4K' }, { height: 0, label: 'Native' },
];
/** Resolution target (canvas height, 0 = native) — see TierPreset.targetHeight. */
export function setTargetHeight(h: number): void { settings_.targetHeight = clampNum(Math.round(h), 0, 4320); markCustom(); } // minimal tier (2026-09-08): with FSR at 2× this is 0.175 of the display per axis
export function setRenderScale(v: number): void { settings_.pixelRatio = clampNum(v, MIN_RENDER_SCALE, 2); settings_.targetHeight = 0; markCustom(); } // the slider = ratio mode
export function setUpscale(m: UpscaleMode): void { settings_.upscale = UPSCALE_RATIO[m] !== undefined ? m : 'off'; markCustom(); }
export function setAdaptive(on: boolean): void { settings_.adaptive = on; markCustom(); }
/** Distant detail (HLOD / far terrain past the draw distance) on or off — see TierPreset.distantDetail. */
export function setDistantDetail(on: boolean): void { settings_.distantDetail = on; markCustom(); }
/** True unless the player turned distant detail off (older saved settings lack the field = on). */
export function distantDetailOn(): boolean { return settings_.distantDetail !== false; }
export function setPlacementLod(l: 0 | 1 | 2 | 3): void { settings_.placementLod = l; markCustom(); }
export function setSettlementBuildLod(l: 1 | 2): void { settings_.settlementBuildLod = l; markCustom(); }
export function setRiverWater(m: 'realistic' | 'simple'): void { settings_.riverWater = m; markCustom(); }
export function setGroundQuality(q: 'full' | 'simple'): void { settings_.groundQuality = q; markCustom(); }
export function setSkyQuality(q: 'full' | 'flat'): void { settings_.skyQuality = q; markCustom(); }
export function setUpscaleMode(m: 'fsr' | 'taau'): void { settings_.upscaleMode = m === 'taau' ? 'taau' : 'fsr'; markCustom(); }
export function setCloudResolution(v: number): void { settings_.cloudResolution = clampNum(v, 0.25, 1); markCustom(); }
export function setAA(mode: AAMode): void {
  const post = { ...settings_.post };
  delete post.fxaa; delete post.smaa; delete post.traa;
  if (mode === 'fxaa') post.fxaa = { enabled: true, quality: settings_.aaQuality };
  else if (mode === 'smaa') post.smaa = { enabled: true, quality: settings_.aaQuality };
  // TRAA needs the velocity MRT; the pipeline falls back to FXAA when the
  // target is unavailable (its own guard) — safe to request unconditionally.
  else if (mode === 'traa') post.traa = { enabled: true, quality: settings_.aaQuality };
  // 'off' must be EXPLICIT: the engine applies FXAA by default unless
  // `fxaa.enabled === false` (PostProcessingPipeline 24c) — deleting both keys
  // silently left FXAA running while the menu (and currentAA) claimed "off".
  else post.fxaa = { enabled: false };
  settings_.post = post; markCustom();
}
export function setBloom(on: boolean): void {
  const post = { ...settings_.post };
  if (on) post.bloom = post.bloom ?? { threshold: 1.1, strength: 0.5, radius: 0.6 };
  else delete post.bloom;
  settings_.post = post; markCustom();
}
export function setSSAO(on: boolean): void {
  const post = { ...settings_.post };
  if (on) post.ao = post.ao ?? { intensity: 1.0, radius: 0.5, samples: 16 };
  else delete post.ao;
  settings_.post = post; markCustom();
}
export function setDoF(on: boolean): void {
  const post = { ...settings_.post };
  if (on) post.dof = post.dof ?? { focusDistance: 18, focalLength: 55, bokehScale: 1.2 };
  else delete post.dof;
  settings_.post = post; markCustom();
}
export function setClouds(on: boolean): void { settings_.cloudsEnabled = on; markCustom(); }
export function setLowClouds(on: boolean): void { settings_.lowClouds = on; markCustom(); }
export function setAAQuality(q: number): void {
  settings_.aaQuality = [2, 4, 8, 16, 32].includes(q) ? q : 2;
  const post = { ...settings_.post };
  if (post.fxaa?.enabled) post.fxaa = { ...post.fxaa, quality: settings_.aaQuality };
  if (post.smaa?.enabled) post.smaa = { ...post.smaa, quality: settings_.aaQuality };
  if (post.traa?.enabled) post.traa = { ...post.traa, quality: settings_.aaQuality };
  settings_.post = post; markCustom();
}
/** Distance fog: metres to the ~63% extinction depth; 0 turns the fog off.
 *  Maps inversely onto fogScale (AtmosphereModule multiplies its tod+weather
 *  density by it; 1200 m ≈ the tier-1.0 baseline). */
export function setFogDistance(m: number): void {
  settings_.fogScale = m <= 0 ? 0 : Math.min(3, Math.max(0.3, 1200 / m));
  markCustom();
}
export function setVolFogDistance(m: number): void {
  const post = { ...settings_.post };
  if (post.volumetricFog) post.volumetricFog = { ...post.volumetricFog, density: 2.5 / Math.max(60, Math.min(600, m)) };
  settings_.post = post; markCustom();
}
export function setBloomStrength(v: number): void {
  const post = { ...settings_.post };
  if (post.bloom) post.bloom = { ...post.bloom, strength: clampNum(v, 0, 2) };
  settings_.post = post; markCustom();
}
export function setMotionBlurIntensity(v: number): void {
  const post = { ...settings_.post };
  if (post.motionBlur) post.motionBlur = { ...post.motionBlur, intensity: clampNum(v, 0.25, 3) };
  settings_.post = post; markCustom();
}
export function setAutoExposureStrength(v: number): void { settings_.autoExposureStrength = clampNum(v, 0, 1); markCustom(); }
export function setDofFocusDistance(v: number): void {
  const post = { ...settings_.post };
  // focusDistance is in world/view metres (CoC = cocScale·(1 − focusDistance/z));
  // allow the focus plane out to 4 km so distant vistas can be brought into focus.
  if (post.dof) post.dof = { ...post.dof, focusDistance: clampNum(v, 1, 4000) };
  settings_.post = post; markCustom();
}
export function setDofFocalLength(v: number): void {
  const post = { ...settings_.post };
  // Metres of focus RANGE (see the high preset). The old 0.005–0.3 range was
  // built around reading this as a lens focal length, so every value on the
  // slider meant "fully blurred within 30 cm of the focus plane".
  if (post.dof) post.dof = { ...post.dof, focalLength: clampNum(v, 4, 400) };
  settings_.post = post; markCustom();
}
export function setDofBokehScale(v: number): void {
  const post = { ...settings_.post };
  if (post.dof) post.dof = { ...post.dof, bokehScale: clampNum(v, 0.3, 4) };
  settings_.post = post; markCustom();
}
export function setCloudParallax(v: number): void { settings_.cloudParallax = clampNum(v, 0, 4); markCustom(); }
export function setModelSmoothing(on: boolean): void { settings_.meshSmoothing = on; markCustom(); }
export function setSurfaceDetail(on: boolean): void { settings_.surfaceDetail = on; markCustom(); }
export function setParticleDensity(v: number): void { settings_.particleCap = clampNum(v, 0, 1); markCustom(); }
/**
 * CUSTOM draw distance (metres, up to 5 km) — the "see far on ANY preset" lever,
 * independent of the graphics tier. Overrides the terrain streaming window
 * (~m/256 chunks) AND the object far-cull; scales settlements up to a moderate
 * cap (they're draw-item heavy); and thins the fog so it never sits NEARER than
 * the draw distance (raising it behind a wall of fog would show nothing).
 */
export const MIN_DRAW_DISTANCE_M = 500; // user 2026-09-08: never nearer than 500 m (objects and the void alike)
export function setDrawDistance(m: number): void {
  const d = clampNum(m, MIN_DRAW_DISTANCE_M, 5000);
  settings_.chunkRadius = Math.max(2, Math.round(d / 256)); // 2 chunks = the 512 m window of the minimal tier
  settings_.farCull = Math.round(d);
  settings_.settlementRadiusScale = clampNum(d / 1000, 1, 3);
  if (settings_.fogScale > 0) settings_.fogScale = Math.min(settings_.fogScale, 1200 / d);
  markCustom();
}
/** Current custom draw distance in metres (the terrain window). */
export function currentDrawDistance(): number { return Math.round((settings_.chunkRadius ?? 3) * 256); }
/** Shadow quality maps to per-cascade map size + cascade count + far distance; 'off' disables CSM entirely. */
export function setShadows(level: ShadowLevel): void {
  if (level === 'off') {
    // Potato-style: no CSM node at all — LightingModule tears the cascades down.
    settings_.shadowsEnabled = false; markCustom(); return;
  }
  const map: Record<Exclude<ShadowLevel, 'off'>, { shadowMapSize: number; shadowCascades: number; shadowMaxFar: number }> = {
    low:   { shadowMapSize: 1024, shadowCascades: 2, shadowMaxFar: 150 },
    med:   { shadowMapSize: 2048, shadowCascades: 3, shadowMaxFar: 200 },
    high:  { shadowMapSize: 2048, shadowCascades: 4, shadowMaxFar: 250 },
    ultra: { shadowMapSize: 4096, shadowCascades: 4, shadowMaxFar: 350 },
  };
  Object.assign(settings_, map[level], { shadowsEnabled: true }); markCustom();
}

// ── Shader-quality setters (2026-08-17 gap-closure wave) ─────────────────────
// Each maps a menu control onto the engine pipeline's new pass configs.

export function setVignette(on: boolean): void {
  const post = { ...settings_.post };
  if (on) post.vignette = post.vignette ?? { strength: 0.4, radius: 0.4, softness: 0.45 };
  else delete post.vignette;
  settings_.post = post; markCustom();
}
/** 0 disables; otherwise the RCAS-style sharpen strength. */
export function setSharpen(strength: number): void {
  const post = { ...settings_.post };
  if (strength > 0) post.sharpen = { strength: clampNum(strength, 0, 1) };
  else delete post.sharpen;
  settings_.post = post; markCustom();
}
// ── Graphics-menu completion wave: the knobs that existed but had no control ──
export function setFoliageDensity(v: number): void { settings_.foliageDensityScale = clampNum(v, 0, 1.5); markCustom(); }
export function setLodDistanceScale(v: number): void { settings_.lodDistanceScale = clampNum(v, 0.5, 6); markCustom(); }
export function setSettlementDetail(v: number): void { settings_.settlementRadiusScale = clampNum(v, 0.5, 3); markCustom(); }
export function setSettlementLowLod(on: boolean): void { settings_.settlementForceLowLod = on; markCustom(); }
export function setNearFoliageLod(lod: 0 | 1): void { settings_.nearFoliageLod = lod; markCustom(); }
export function setPropLod(lod: 0 | 1 | 2): void { settings_.propLod = lod; markCustom(); }
export function setMaxTextureRes(v: number): void {
  settings_.maxTextureRes = [512, 1024, 2048, 4096].includes(v) ? v : 2048;
  markCustom();
}
export function setAnisotropy(v: number): void {
  settings_.anisotropy = [1, 2, 4, 8, 16].includes(v) ? v : 4;
  markCustom();
}
export function setTonemapExposure(v: number): void { settings_.tonemapExposure = clampNum(v, 0.4, 1.8); markCustom(); }
export function setTonemapOperator(op: 'aces' | 'agx' | 'neutral' | 'reinhard'): void { settings_.tonemapOperator = op; markCustom(); }
export function setCloudMarchQuality(q: 'auto' | 'low' | 'medium' | 'high'): void { settings_.cloudMarchQuality = q; markCustom(); }
export function setWaterReflections(on: boolean): void { settings_.waterReflections = on; markCustom(); }
export function setColorblindMode(m: 'off' | 'protanopia' | 'deuteranopia' | 'tritanopia'): void { settings_.colorblindMode = m; markCustom(); }
export function setColorblindStrength(v: number): void { settings_.colorblindStrength = clampNum(v, 0, 1); markCustom(); }
export function setCameraFov(deg: number): void { settings_.cameraFov = deg <= 0 ? 0 : clampNum(deg, 50, 100); markCustom(); }
export function setAutoTune(on: boolean): void {
  // Persist WITHOUT marking custom: the tuner must survive its own setTier
  // steps (custom disables the ladder walk to respect manual tweaks).
  settings_.autoTune = on;
  saveSettings();
}
// Shader-parameter sliders over passes the toggles already own:
export function setBloomThreshold(v: number): void {
  const post = { ...settings_.post };
  if (post.bloom) post.bloom = { ...post.bloom, threshold: clampNum(v, 0.4, 1.6) };
  settings_.post = post; markCustom();
}
export function setBloomRadius(v: number): void {
  const post = { ...settings_.post };
  if (post.bloom) post.bloom = { ...post.bloom, radius: clampNum(v, 0, 1) };
  settings_.post = post; markCustom();
}
export function setAoIntensity(v: number): void {
  const post = { ...settings_.post };
  if (post.ao) post.ao = { ...post.ao, intensity: clampNum(v, 0, 3) };
  settings_.post = post; markCustom();
}
export function setAoRadius(v: number): void {
  const post = { ...settings_.post };
  if (post.ao) post.ao = { ...post.ao, radius: clampNum(v, 0.1, 2) };
  settings_.post = post; markCustom();
}
export function setVignetteStrength(v: number): void {
  const post = { ...settings_.post };
  if (post.vignette) post.vignette = { ...post.vignette, strength: clampNum(v, 0, 1) };
  settings_.post = post; markCustom();
}
export function setFilmGrainIntensity(v: number): void {
  const post = { ...settings_.post };
  if (post.film) post.film = { ...post.film, intensity: clampNum(v, 0, 1) };
  settings_.post = post; markCustom();
}
export function setChromaticStrength(v: number): void {
  const post = { ...settings_.post };
  if (post.chromaticAberration) post.chromaticAberration = { ...post.chromaticAberration, strength: clampNum(v, 0, 2) };
  settings_.post = post; markCustom();
}
export function setMotionBlurSamples(v: number): void {
  const post = { ...settings_.post };
  if (post.motionBlur) post.motionBlur = { ...post.motionBlur, samples: Math.round(clampNum(v, 4, 32)) };
  settings_.post = post; markCustom();
}
// Engine passes never wired before (config-key presence = pass included):
export function setSsr(on: boolean): void {
  const post = { ...settings_.post };
  if (on) post.ssr = post.ssr ?? { intensity: 1.0, maxDistance: 40, thickness: 0.35, resolutionScale: 0.5 };
  else delete post.ssr;
  settings_.post = post; markCustom();
}
export function setContactShadows(on: boolean): void {
  const post = { ...settings_.post };
  if (on) post.contactShadows = post.contactShadows ?? { enabled: true };
  else delete post.contactShadows;
  settings_.post = post; markCustom();
}
export function setSsgi(on: boolean): void {
  const post = { ...settings_.post };
  if (on) post.ssgi = post.ssgi ?? { enabled: true };
  else delete post.ssgi;
  settings_.post = post; markCustom();
}
export function setLensFlares(on: boolean): void {
  const post = { ...settings_.post };
  if (on) post.lensFlares = post.lensFlares ?? { enabled: true };
  else delete post.lensFlares;
  settings_.post = post; markCustom();
}
/** The colour look — a procedural 3D LUT for the engine's lut3D pass (PostModule bakes it). */
export function setLook(look: TierPreset['look']): void { settings_.look = look; markCustom(); }
export function setFoliageShading(on: boolean): void { settings_.foliageShading = on; markCustom(); }
export function setFoliageWind(on: boolean): void { settings_.foliageWind = on; markCustom(); }
/** Whether foliage wind should run: its own switch, defaulting to the shading flag. */
export function foliageWindOn(preset = settings_): boolean {
  return preset.foliageWind ?? (preset.foliageShading !== false);
}
export function currentFoliageWind(): boolean { return foliageWindOn(); }
export function setFluidPhysics(on: boolean): void { settings_.fluidPhysics = on; markCustom(); }
export function setMaterialFx(on: boolean): void { settings_.materialFx = on; markCustom(); }
// Shader wave stage 3 (2026-09-09): the remaining engine features.
export function setInteriorMapping(on: boolean): void { settings_.interiorMapping = on; markCustom(); }
export function setVfxShaders(on: boolean): void { settings_.vfxShaders = on; markCustom(); }
export function setLitDecals(on: boolean): void { settings_.litDecals = on; markCustom(); }
export function setLampCookies(on: boolean): void { settings_.lampCookies = on; markCustom(); }
export function setWaterFoam(on: boolean): void { settings_.waterFoam = on; markCustom(); }
export function setWaterCaustics(on: boolean): void { settings_.waterCaustics = on; markCustom(); }
// Shader wave stage 4 (2026-09-09): the new engine shaders.
export function setCloudShadows(on: boolean): void { settings_.cloudShadows = on; markCustom(); }
export function setGroundAntiTiling(on: boolean): void { settings_.groundAntiTiling = on; markCustom(); }
export function setRainRipples(on: boolean): void { settings_.rainRipples = on; markCustom(); }
export function setAurora(on: boolean): void { settings_.aurora = on; markCustom(); }
export function setNebulae(on: boolean): void { settings_.nebulae = on; markCustom(); }
export function setPlanetAtmosphere(on: boolean): void { settings_.planetAtmosphere = on; markCustom(); }
export function setUnderwaterShafts(on: boolean): void { settings_.underwaterShafts = on; markCustom(); }
export function setDqSkinning(on: boolean): void { settings_.dqSkinning = on; markCustom(); }
// Shader additions wave (2026-09-15).
export function setSpecularAA(on: boolean): void { settings_.specularAA = on; markCustom(); }
export function setProceduralFiltering(on: boolean): void { settings_.proceduralFiltering = on; markCustom(); }
export function setVolumetricLights(on: boolean): void { settings_.volumetricLights = on; markCustom(); }
export function setLitSmoke(on: boolean): void { settings_.litSmoke = on; markCustom(); }
export function setAreaLights(on: boolean): void { settings_.areaLights = on; markCustom(); }
export function setGlassTransmission(on: boolean): void { settings_.glassTransmission = on; markCustom(); }
export function setHardSurfaceAnisotropy(on: boolean): void { settings_.hardSurfaceAnisotropy = on; markCustom(); }
export function setRetroreflection(on: boolean): void { settings_.retroreflection = on; markCustom(); }
// Render wave 1 (2026-09-17).
export function setImpostorBlend(on: boolean): void { settings_.impostorBlend = on; markCustom(); }
export function setRainShelter(on: boolean): void { settings_.rainShelter = on; markCustom(); }
export function setAutoExposureMetering(m: 'average' | 'histogram'): void { settings_.autoExposureMetering = m; markCustom(); }
export function currentAutoExposureMetering(): 'average' | 'histogram' { return settings_.autoExposureMetering ?? 'average'; }
/** The bloom filter: three's Gaussian mip chain or the dual-Kawase blur (only meaningful while bloom is on). */
export function setBloomMode(mode: 'mip' | 'kawase'): void {
  const post = { ...settings_.post };
  if (post.bloom) post.bloom = { ...post.bloom, mode };
  settings_.post = post; markCustom();
}
export function currentBloomMode(): 'mip' | 'kawase' { return (settings_.post.bloom?.mode as 'mip' | 'kawase' | undefined) ?? 'mip'; }
export function setGodRays(on: boolean): void {
  const post = { ...settings_.post };
  if (on) post.godRays = post.godRays ?? { enabled: true, numSamples: 48, decay: 0.965, exposure: 0.22, density: 1.0, weight: 0.01 };
  else delete post.godRays;
  settings_.post = post; markCustom();
}

/** 1 = neutral (removes the grade pass); else a saturation-only colour grade. */
export function setSaturation(v: number): void {
  const post = { ...settings_.post };
  const sat = clampNum(v, 0.5, 1.5);
  if (Math.abs(sat - 1) < 0.01) delete post.colorGrade;
  else post.colorGrade = { saturation: sat };
  settings_.post = post; markCustom();
}
export function setVolumetricFogPass(on: boolean): void {
  const post = { ...settings_.post };
  if (on) post.volumetricFog = post.volumetricFog ?? { density: 0.012, heightFalloff: 0.05, sunPower: 8 };
  else delete post.volumetricFog;
  settings_.post = post; markCustom();
}
export function setMotionBlur(on: boolean): void {
  const post = { ...settings_.post };
  if (on) post.motionBlur = post.motionBlur ?? { samples: 12 };
  else delete post.motionBlur;
  settings_.post = post; markCustom();
}
export function setFilmGrain(on: boolean): void {
  const post = { ...settings_.post };
  if (on) post.film = post.film ?? { intensity: 0.35 };
  else delete post.film;
  settings_.post = post; markCustom();
}
export function setChromaticAberration(on: boolean): void {
  const post = { ...settings_.post };
  if (on) post.chromaticAberration = post.chromaticAberration ?? { strength: 0.6, scale: 1.04 };
  else delete post.chromaticAberration;
  settings_.post = post; markCustom();
}
export function setCapsuleShadows(on: boolean): void {
  const post = { ...settings_.post };
  if (on) post.capsuleShadows = post.capsuleShadows ?? { intensity: 0.55, penumbra: 0.3, maxDistance: 5 };
  else delete post.capsuleShadows;
  settings_.post = post; markCustom();
}
/** Applies at next reload — the sky bakes the froxel volume only when created with it on. */
export function setAerialPerspective(on: boolean): void { settings_.aerialEnabled = on; markCustom(); }
export function setAutoExposure(on: boolean): void { settings_.autoExposure = on; markCustom(); }
/** Bokeh style for the DoF pass (only meaningful while DoF is on). */
export function setBokehStyle(style: 'standard' | 'hex'): void {
  const post = { ...settings_.post };
  if (post.dof) post.dof = { ...post.dof, bokeh: style };
  settings_.post = post; markCustom();
}

export function currentVignette(): boolean { return !!settings_.post.vignette; }
export function currentSharpen(): number { return settings_.post.sharpen?.strength ?? 0; }
export function currentSaturation(): number { return settings_.post.colorGrade?.saturation ?? 1; }
export function currentVolumetricFogPass(): boolean { return !!settings_.post.volumetricFog; }
export function currentMotionBlur(): boolean { return !!settings_.post.motionBlur; }
export function currentFilmGrain(): boolean { return !!settings_.post.film; }
export function currentChromaticAberration(): boolean { return !!settings_.post.chromaticAberration; }
export function currentCapsuleShadows(): boolean { return !!settings_.post.capsuleShadows; }
export function currentAerialPerspective(): boolean { return settings_.aerialEnabled; }
export function currentAutoExposure(): boolean { return settings_.autoExposure; }
export function currentLowClouds(): boolean { return settings_.lowClouds; }
export function currentAAQuality(): number { return settings_.aaQuality; }
export function currentFogDistance(): number { return settings_.fogScale <= 0 ? 0 : Math.round(1200 / settings_.fogScale); }
export function currentVolFogDistance(): number { const d = settings_.post.volumetricFog?.density; return d ? Math.round(2.5 / d) : 0; }
export function currentBloomStrength(): number { return settings_.post.bloom?.strength ?? 0; }
export function currentMotionBlurIntensity(): number { return settings_.post.motionBlur?.intensity ?? 1; }
export function currentAutoExposureStrength(): number { return settings_.autoExposureStrength; }
export function currentDofFocusDistance(): number { return settings_.post.dof?.focusDistance ?? 18; }
export function currentDofFocalLength(): number { return settings_.post.dof?.focalLength ?? 55; }
export function currentDofBokehScale(): number { return settings_.post.dof?.bokehScale ?? 1.2; }
export function currentCloudParallax(): number { return settings_.cloudParallax; }
export function currentBokehStyle(): 'standard' | 'hex' { return settings_.post.dof?.bokeh ?? 'standard'; }

/** Current per-feature values, for the menu to render its controls from. */
export function currentAA(): AAMode {
  if (settings_.post.traa?.enabled) return 'traa';
  if (settings_.post.smaa?.enabled) return 'smaa';
  if (settings_.post.fxaa && settings_.post.fxaa.enabled === false) return 'off';
  // No explicit off ⇒ the engine's default FXAA runs (even with no fxaa key).
  return 'fxaa';
}
export function currentShadows(): ShadowLevel {
  if (!settings_.shadowsEnabled) return 'off';
  const s = settings_.shadowMapSize, c = settings_.shadowCascades;
  if (s >= 4096) return 'ultra';
  if (s >= 2048 && c >= 4) return 'high';
  if (s >= 2048) return 'med';
  return 'low';
}
export function currentModelSmoothing(): boolean { return settings_.meshSmoothing; }
export function currentSurfaceDetail(): boolean { return settings_.surfaceDetail; }

// ── Persistence ───────────────────────────────────────────────────────────────
const LS_KEY = 'atlas:graphics';

/** Persist the active tier + the live settings (so custom tweaks survive a reload). */
export function saveSettings(): void {
  if (inDraft_) return; // a draft is not the player's settings until "Apply changes"
  liveStamp_++;         // every live mutation persists through here: an open draft re-bases on it
  try {
    if (typeof localStorage === 'undefined') return;
    localStorage.setItem(LS_KEY, JSON.stringify({ baseTier: baseTier_, custom: custom_, settings: settings_ }));
  } catch { /* storage blocked — settings just won't persist */ }
}

/**
 * True when this browser has locally-saved graphics settings. The server's
 * device_tier_hint must only seed a tier for a FIRST-ever login — applying it
 * over saved settings stomped the player's persisted/custom choices every
 * session (and reverted any tier outside the hint's low|med|high vocabulary).
 */
export function hasSavedSettings(): boolean {
  try {
    return typeof localStorage !== 'undefined' && localStorage.getItem(LS_KEY) !== null;
  } catch { return false; }
}

/** Restore persisted settings, or auto-detect a starting preset on first run. Call once at boot. */
export function loadSettings(): void {
  try {
    const raw = typeof localStorage !== 'undefined' ? localStorage.getItem(LS_KEY) : null;
    if (raw) {
      const parsed = JSON.parse(raw) as { baseTier?: Tier; custom?: boolean; settings?: Partial<TierPreset> };
      if (parsed.baseTier && TIERS[parsed.baseTier]) {
        baseTier_ = parsed.baseTier;
        custom_ = !!parsed.custom;
        // Start from a clean base preset, then overlay the persisted (possibly customised) fields —
        // so a TierPreset schema change adds new fields with sane preset defaults instead of `undefined`.
        settings_ = { ...clonePreset(TIERS[baseTier_]), ...(parsed.settings ?? {}), post: { ...TIERS[baseTier_].post, ...(parsed.settings?.post ?? {}) } };
        // One-shot migrations (2026-08-18/19): 2.5, 0 and 0.15 were each the
        // shipped cloudParallax default for a few hours/days (exaggerated-
        // parallax, then fully-frozen, then travel-evolution) before the
        // world-anchored default (1.0) — a persisted 2.5/0/0.15 was almost
        // certainly never a deliberate choice; let the preset default flow.
        // Any other stored value is a real choice and is kept.
        if (parsed.settings?.cloudParallax === 2.5 || parsed.settings?.cloudParallax === 0 || parsed.settings?.cloudParallax === 0.15) {
          settings_.cloudParallax = TIERS[baseTier_].cloudParallax;
        }
        return;
      }
    }
  } catch { /* corrupt/blocked — fall through to auto-detect */ }
  // First run (or unreadable): auto-detect from the device — unless a browser under AUTOMATION, which
  // boots on the minimal tier (THE PROBE TIER LAW: a fresh headless profile once auto-detected ULTRA and
  // swiftshader died carrying its post chain; a probe that wants a tier seeds 'atlas:graphics').
  setActiveTier(defaultTierFor(underAutomation(), detectDefaultTier()));
}

/** navigator.webdriver — puppeteer / playwright / any WebDriver session. */
export function underAutomation(): boolean {
  return typeof navigator !== 'undefined' && (navigator as { webdriver?: boolean }).webdriver === true;
}
/** The boot tier with nothing saved: the detected one for a person, 'minimal' for a robot. */
export function defaultTierFor(automation: boolean, detected: Tier): Tier {
  return automation ? 'minimal' : detected;
}

/**
 * Pick a sensible starting preset from device capability so a weak machine isn't
 * dropped into Ultra. Heuristic only — the player can override in the menu.
 */
export function detectDefaultTier(): Tier {
  let cores = 4, mem = 4, mobile = false;
  try {
    if (typeof navigator !== 'undefined') {
      cores = (navigator.hardwareConcurrency as number) || 4;
      mem = ((navigator as unknown as { deviceMemory?: number }).deviceMemory) || 4; // GB, Chromium-only
    }
    mobile = typeof matchMedia !== 'undefined' && matchMedia('(pointer: coarse)').matches;
  } catch { /* defaults */ }
  if (mobile || cores <= 2 || mem <= 2) return 'low';
  if (cores <= 4 || mem <= 4) return 'med';
  if (cores >= 12 && mem >= 8) return 'ultra';
  return 'high';
}

function clampNum(v: number, lo: number, hi: number): number { return v < lo ? lo : v > hi ? hi : v; }
